import React, { useEffect, useMemo } from 'react';
import { atom, useAtomValue } from 'jotai';
import { useLocation } from 'react-router-dom';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from '../../theme';
import { Branding, ensureFontLoaded } from '../../services/branding.service';
import { hackathonsAtom } from '../../services/hackathon.service';
import { findHackathon } from '../../hooks/useHackathon';

/**
 * Branding returned with 401/403 responses, keyed by hackathon id/slug, so sign-in and join
 * screens are branded before the user can load the hackathon itself.
 */
export const gateBrandingAtom = atom<Record<string, Branding>>({});

export const useCurrentHackathonId = () => {
    const location = useLocation();
    return useMemo(() => {
        if (location.pathname.startsWith('/h/')) {
            return location.pathname.split('/')[2] || '';
        }
        return '';
    }, [location.pathname]);
}

export const BrandedThemeProvider = ({
    branding,
    children
}: {
    branding?: Branding,
    children: React.ReactNode
}) => {
    const key = JSON.stringify(branding || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const theme = useMemo(() => createAppTheme(branding), [key]);

    useEffect(() => {
        ensureFontLoaded(branding?.fontFamily);
    }, [branding?.fontFamily]);

    return <ThemeProvider theme={theme}>{children}</ThemeProvider>
}

/**
 * Applies the current hackathon's branding (if any) to the whole app, including header and footer.
 * Reads already-loaded hackathons from the store rather than fetching.
 */
export const AppThemeProvider = ({ children }: { children: React.ReactNode }) => {
    const hackathonId = useCurrentHackathonId();
    const hackathons = useAtomValue(hackathonsAtom);
    const gateBranding = useAtomValue(gateBrandingAtom);

    const branding = useMemo(() => {
        if (!hackathonId) {
            return undefined;
        }
        const hackathon = findHackathon(hackathons, hackathonId);
        return hackathon?.resolvedBranding || gateBranding[hackathonId];
    }, [hackathonId, hackathons, gateBranding]);

    return (
        <BrandedThemeProvider branding={branding}>
            <CssBaseline />
            {children}
        </BrandedThemeProvider>
    )
}
